import React, {
  createContext,
  FC,
  ReactNode,
  useContext,
  useEffect,
} from "react";
import { useLazyQuery, useMutation } from "@apollo/client";
import FetalSexService from "@app/service/fetalSex";
import { NotificationContext } from "@app/provider";
import { AddNotificationParameters } from "@app/provider/notification";
import { getExtendedServerErrorNotificationParams } from "@app/utils/getExtendedServerErrorNotificationParams";

interface UpdateFetalSex {
  testUid: string;
  fetalSexIsShown: boolean;
  documentUid: string;
}

export interface FetalSexController {
  getFetalSexIsShown: (
    testUid: string
  ) => Promise<Record<string, string | boolean> | undefined>;
  handleUpdateFetalSexIsShown: (
    orderUidPublic: string,
    testUid: string,
    fetalSexIsShown: boolean
  ) => Promise<UpdateFetalSex | undefined>;
  updateFetalSexIsShownIsLoading: boolean;
}
export const Context = createContext<FetalSexController>({
  getFetalSexIsShown: () => Promise.reject(),
  handleUpdateFetalSexIsShown: () => Promise.reject(),
  updateFetalSexIsShownIsLoading: false,
});

Context.displayName = "FetalSexContext";

const FetalSexProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const { addNotification } = useContext(NotificationContext);

  const [
    updateFetalSexIsShown,
    {
      error: updateFetalSexIsShownError,
      loading: updateFetalSexIsShownIsLoading,
    },
  ] = useMutation<{ updateFetalSexIsShown: UpdateFetalSex | undefined }>(
    FetalSexService.updateFetalSexIsShown()
  );

  useEffect(() => {
    if (updateFetalSexIsShownError) {
      const notificationParams: AddNotificationParameters =
        getExtendedServerErrorNotificationParams(updateFetalSexIsShownError);
      addNotification(notificationParams);
    }
  }, [updateFetalSexIsShownError]);

  const handleUpdateFetalSexIsShown = async (
    orderUidPublic: string,
    testUid: string,
    fetalSexIsShown: boolean
  ) => {
    try {
      const res = await updateFetalSexIsShown({
        variables: {
          orderUidPublic,
          testUid,
          fetalSexIsShown,
        },
      });

      if (!res.data) {
        throw new Error();
      }

      return res.data.updateFetalSexIsShown;
    } catch (error) {
      throw new Error(error);
    }
  };

  const [getFetalSexQuery] = useLazyQuery<{
    getFetalSexIsShown: Record<string, string | boolean>;
  }>(FetalSexService.getFetalSex(), {
    fetchPolicy: "no-cache",
  });

  const getFetalSexIsShown = async (orderUidPublic: string) => {
    const result = await getFetalSexQuery({
      variables: {
        orderUidPublic,
      },
    });

    return result.data?.getFetalSexIsShown;
  };

  return (
    <Context.Provider
      value={{
        getFetalSexIsShown,
        handleUpdateFetalSexIsShown,
        updateFetalSexIsShownIsLoading,
      }}
    >
      {children}
    </Context.Provider>
  );
};

export default FetalSexProvider;
